"use client";

import { Play } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Awareness() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: wordRef,
    isVisible: wordVisible
  } = useRevealOnScroll<HTMLSpanElement>({
    threshold: 0.15
  });
  const ledger = [{
    label: "12 CAUSES"
  }, {
    label: "MONTHLY"
  }, {
    label: "IMPACT"
  }, {
    label: "COMMUNITY"
  }];
  return <section data-section-id="1858" id="bloghero" className="relative overflow-hidden bg-background text-foreground py-32 md:py-44">
      {}
      <svg aria-hidden="true" className="pointer-events-none absolute left-0 top-0 h-full w-24 md:w-40 opacity-40" viewBox="0 0 160 800" fill="none" preserveAspectRatio="none">
        <path d="M40 0 C 70 120, 20 220, 60 340 S 100 560, 50 700 L 50 800" stroke="currentColor" strokeWidth="1" className="text-foreground/40" />
      </svg>

      {}
      <svg aria-hidden="true" className="pointer-events-none absolute -right-24 top-16 hidden md:block h-[720px] w-[640px] opacity-60" viewBox="0 0 640 720" fill="none">
        <path d="M520 90 C 600 160, 640 280, 600 400 C 560 540, 440 640, 300 660 C 180 680, 60 600, 40 460 C 20 320, 80 180, 200 110 C 320 40, 440 30, 520 90 Z" fill="currentColor" className="text-foreground/10" />
      </svg>

      <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 md:px-12 lg:px-[72px]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {}
          <div ref={leftRef} className="lg:col-span-8 transition-all duration-[720ms] ease-out" style={{
          opacity: leftVisible ? 1 : 0,
          transform: leftVisible ? "translateY(0)" : "translateY(16px)"
        }}>
            {}
            <div className="flex items-center gap-3 mb-10">
              <span className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-foreground/70">
                Forever Fresh · Mission
              </span>
              <span className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-foreground/70">
                · §01 — ADVOCACY
              </span>
            </div>

            {}
            <h1 className="font-default font-normal text-foreground leading-[1.02] tracking-[-0.025em] mb-10" style={{
            fontSize: "clamp(2.75rem, 6.4vw, 5.75rem)"
          }}>
              Awareness News
            </h1>

            {}
            <p className="font-default text-foreground/75 max-w-[480px] mb-12 leading-[1.65]" style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)"
          }}>
              This page shares current articles, updates, and stories connected to the causes Forever Fresh is spotlighting right now.
            </p>

            {}
            <div className="flex flex-wrap items-center gap-4 mb-14">
              <Button asChild className="h-[52px] rounded-lg px-7 bg-primary text-primary-foreground hover:bg-primary/90 font-medium tracking-[0.01em] transition-all duration-220 hover:-translate-y-[1.5px]">
                <Link to="/CurrentCausesPage">Explore Causes</Link>
              </Button>
              <Button asChild variant="outline" className="h-[52px] rounded-lg px-7 bg-transparent border border-foreground/60 text-foreground hover:bg-transparent hover:border-primary hover:text-primary font-medium tracking-[0.01em] transition-all duration-220">
                <Link to="/ShopPage">
                  <Play className="size-3.5" strokeWidth={1.5} />
                  Shop Current Drop
                </Link>
              </Button>
            </div>

            {}
            <div className="pt-6 border-t border-foreground/20">
              <div className="flex items-center gap-3 mb-3">
                <span className="block h-1.5 w-1.5 rounded-lg bg-primary" />
                <span className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-foreground/60">
                  Current Campaign
                </span>
                <span className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-foreground/60">
                  · Featured Cause — Women&apos;s Equality monthly campaign
                </span>
              </div>
              <p className="font-default text-sm text-foreground/70 mb-6">
                Discover featured articles and browse related news stories here.
              </p>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                {ledger.map((item, i) => <span data-index={i} key={item.label} className="font-mono text-xs sm:text-sm tracking-[0.06em] text-foreground/85">
                    {item.label}
                    {i < ledger.length - 1 && <span className="ml-6 text-foreground/30">·</span>}
                  </span>)}
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className="lg:col-span-4 relative transition-all duration-[720ms] ease-out" style={{
          opacity: rightVisible ? 1 : 0,
          transform: rightVisible ? "translateY(0)" : "translateY(16px)",
          transitionDelay: "120ms"
        }}>
            {}
            <svg aria-hidden="true" className="pointer-events-none absolute -inset-8 -z-0" viewBox="0 0 400 500" fill="none" preserveAspectRatio="none">
              <path d="M340 60 C 400 130, 410 260, 360 360 C 310 460, 180 480, 90 420 C 0 360, -10 220, 50 130 C 110 40, 280 -10, 340 60 Z" fill="currentColor" className="text-foreground/10" />
            </svg>

            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/humanitarian_awareness_community.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/humanitarian_awareness_community.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1717563/img/humanitarian_awareness_community.png-wvc-sizes" alt="Community members gathering for a humanitarian awareness event" loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </div>

            {}
            <div className="mt-5 flex items-center gap-3 px-1">
              <span className="block h-px w-8 bg-foreground/40" />
              <span className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-foreground/65">
                Impact Report · 2024
              </span>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-foreground/30" />
    </section>;
}